import type { SerializedError } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { ICreateCompanyForm, IInvitationForm } from "components/AdminPanel/types";
import type { IDefaultUserValues } from "components/MemberList/UserFormModal";
import type { ROLES } from "containers/ScopeContext/roles.enum";
import { toastApiError } from "helpers/apiErrorToast";
import { t } from "i18next";
import { toast } from "react-toastify";
import type { ICompany } from "store/settingsStore";
import type { ILog, IUser } from "types";

import type { TFetchStatus, TParamsPagination } from "./types";

export const sendInvitation = createAsyncThunk(
    "adminPanel/invite",
    async (data: IInvitationForm, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/company/members/invite", data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const createCompany = createAsyncThunk(
    "adminPanel/createCompany",
    async (
        data: Omit<ICreateCompanyForm, "legalStatus" | "shareCapital" | "authorizedPublisherSeats">,
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/adminPortal/companies", data);
            if (response.data?.data) {
                return response.data?.data;
            } else {
                throw Error;
            }
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getCompanyList = createAsyncThunk("adminPanel/getCompanyList", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get<{ data: ICompany[] }>(
            import.meta.env.REACT_APP_API_ENDPOINT + "/adminPortal/companies",
        );
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

// Crealo Users
export const getAdminPortalUserList = createAsyncThunk("adminPanel/getUserList", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get<{ result: IUser[] }>(
            import.meta.env.REACT_APP_API_ENDPOINT + "/adminPortal/users",
        );
        return response.data.result;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const refreshAdminPortalUserList = createAsyncThunk(
    "adminPanel/refreshUserList",
    async (_, { rejectWithValue }) => {
        try {
            await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/adminPortal/users/refresh");
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getAdminPortalUserListPaginated = createAsyncThunk(
    "adminPanel/getUserListPaginated",
    async (
        { options }: { options: TParamsPagination & { companyId?: number; accountType?: string } },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/adminPortal/users/paginated", {
                params: options,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const resendWelcomeEmail = createAsyncThunk(
    "adminPanel/resendWelcomeEmail",
    async (data: { username?: string; email?: string }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.put(import.meta.env.REACT_APP_API_ENDPOINT + "/adminPortal/users/resend", data),
                {
                    pending: `Sending email, please wait...`,
                    success: `Welcome email with temporary password successfully resent!`,
                    error: `Error happened, please retry`,
                },
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getUserListByCompany = createAsyncThunk(
    "adminPanel/getUserListByCompany",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get<{ result: IUser[] }>(
                import.meta.env.REACT_APP_API_ENDPOINT + "/company/members",
            );
            // A 200 without the expected list (e.g. an HTML page from a proxy) must not reach callers reducing it.
            if (!Array.isArray(response.data?.result)) return rejectWithValue(response.data);
            return response.data.result;
        } catch (error: any) {
            return rejectWithValue(error.response?.data);
        }
    },
);

export const deleteUser = createAsyncThunk("adminPanel/deleteUser", async (username: string, { rejectWithValue }) => {
    try {
        await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/adminPortal/users?username=${username}`, {
            data: {
                soft: true,
            },
        });
        return username;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const changeAdminCompany = createAsyncThunk(
    "adminPanel/changeAdminCompany",
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                import.meta.env.REACT_APP_API_ENDPOINT + "/adminPortal/companies/change",
                data,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const eraseData = createAsyncThunk(
    "adminPanel/eraseData",
    async ({ type }: { type: string }, { rejectWithValue }) => {
        try {
            const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/adminPortal/erase/${type}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const migrationCompany = createAsyncThunk(
    "adminPanel/migrationCompany",
    async ({ version }: { version: string }, { rejectWithValue }) => {
        try {
            const response = await axios.put(
                `${import.meta.env.REACT_APP_API_ENDPOINT}/adminPortal/migrations?version=${version}`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const getLogList = createAsyncThunk(
    "adminPanel/logList",
    async ({ options }: { options: TParamsPagination }, { rejectWithValue }) => {
        try {
            const response = await axios.get(`${import.meta.env.REACT_APP_API_ENDPOINT}/adminPortal/logs`, {
                params: options,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const autofillCompany = createAsyncThunk(
    "adminPanel/company/autofill",
    async (type: "simple" | "inter-title-inter-channel" = "simple", { rejectWithValue }) => {
        try {
            const url = `${import.meta.env.REACT_APP_API_ENDPOINT}/adminPortal/company/autofill`;

            // if the type is not simple, we add the type argument to the url
            const response = await axios.get(`${url}${type !== "simple" ? `?type=${type}` : ""}`);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const bulkInviteToPortal = createAsyncThunk("adminPanel/bulkInviteToPortal", async (_, { rejectWithValue }) => {
    try {
        const response = await toast.promise(
            axios.put(`${import.meta.env.REACT_APP_API_ENDPOINT}/beneficiary/invite`),
            {
                pending: `Envoi des invitations en cours...`,
                success: `Les invitations ont bien été envoyées !`,
                error: `Une erreur est survenue, merci de réessayer.`,
            },
        );
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const updateUserRole = createAsyncThunk(
    "adminPanel/updateUserRole",
    async ({ userId, role }: { userId: number; role: ROLES | string }, { rejectWithValue }) => {
        try {
            const result = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/company/members/${userId}/role`, {
                role,
            });
            return result.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const askForSeats = createAsyncThunk("adminPanel/askForSeats", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/company/members/request-seats");
        return response.data.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const updateUser = createAsyncThunk(
    "adminPanel/updateUser",
    async ({ userId, req }: { userId: number; req: IDefaultUserValues }, { rejectWithValue }) => {
        try {
            const result = await axios.put(import.meta.env.REACT_APP_API_ENDPOINT + `/users/${userId}`, req);
            return result.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const syncTCPD = createAsyncThunk("adminPanel/syncTCPD", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/adminPortal/sync-tcpd");
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const recomputeStocks = createAsyncThunk("adminPanel/recomputeStocks", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/adminPortal/recompute-stocks");
        return response.data;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const addCompanyPeriodType = createAsyncThunk(
    "adminPanel/addCompanyPeriodType",
    async ({ duration, periodStart }: { duration: number; periodStart: string }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + "/adminPortal/companies/period-types",
                { duration, periodStart },
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export type AppMaintenanceWindow = {
    id: number;
    startsAt: string;
    endsAt: string;
    title: string;
    description: string;
    createdAt: string;
    cancelledAt: string | null;
};

export type CreateAppMaintenanceInput = {
    startsAt: string;
    endsAt: string;
    title: string;
    description: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

const rejectionPayload = (error: unknown): unknown => {
    if (!isRecord(error) || !isRecord(error.response)) return undefined;
    return error.response.data;
};

const isMaintenanceWindow = (value: unknown): value is AppMaintenanceWindow => {
    if (!isRecord(value)) return false;
    return (
        typeof value.id === "number" &&
        typeof value.startsAt === "string" &&
        typeof value.endsAt === "string" &&
        typeof value.title === "string" &&
        typeof value.description === "string" &&
        typeof value.createdAt === "string" &&
        (value.cancelledAt === null || typeof value.cancelledAt === "string")
    );
};

const readMaintenanceList = (value: unknown): AppMaintenanceWindow[] | null => {
    if (!isRecord(value) || !Array.isArray(value.data)) return null;
    if (!value.data.every(isMaintenanceWindow)) return null;
    return value.data;
};

const maintenanceEndpoint = () => import.meta.env.REACT_APP_API_ENDPOINT + "/adminPortal/maintenance";

export const fetchAppMaintenance = createAsyncThunk(
    "adminPanel/fetchAppMaintenance",
    async (_, { rejectWithValue }) => {
        try {
            const response = await axios.get<unknown>(maintenanceEndpoint());
            const windows = readMaintenanceList(response.data);
            if (!windows) return rejectWithValue({ message: "ServerError.InternalServerError" });
            return windows;
        } catch (error: unknown) {
            return rejectWithValue(rejectionPayload(error));
        }
    },
);

export const createAppMaintenance = createAsyncThunk(
    "adminPanel/createAppMaintenance",
    async (schedule: CreateAppMaintenanceInput, { rejectWithValue }) => {
        try {
            const response = await axios.post<unknown>(maintenanceEndpoint(), schedule);
            const body = response.data;
            if (!isRecord(body) || !isMaintenanceWindow(body.data)) {
                return rejectWithValue({ message: "ServerError.InternalServerError" });
            }
            return body.data;
        } catch (error: unknown) {
            return rejectWithValue(rejectionPayload(error));
        }
    },
);

export const finishAppMaintenance = createAsyncThunk(
    "adminPanel/finishAppMaintenance",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await axios.post<unknown>(`${maintenanceEndpoint()}/${id}/finish`);
            const body = response.data;
            if (!isRecord(body) || !isMaintenanceWindow(body.data)) {
                return rejectWithValue({ message: "ServerError.InternalServerError" });
            }
            return body.data;
        } catch (error: unknown) {
            return rejectWithValue(rejectionPayload(error));
        }
    },
);

export const cancelAppMaintenance = createAsyncThunk(
    "adminPanel/cancelAppMaintenance",
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await axios.delete<unknown>(`${maintenanceEndpoint()}/${id}`);
            const body = response.data;
            if (!isRecord(body) || !isMaintenanceWindow(body.data)) {
                return rejectWithValue({ message: "ServerError.InternalServerError" });
            }
            return body.data;
        } catch (error: unknown) {
            return rejectWithValue(rejectionPayload(error));
        }
    },
);

interface IEntityState<T> {
    isLoading: boolean;
    data: T;
    dataMap?: Map<number, T>;
    error: null | SerializedError;
}

interface ICompanyUsersState {
    isLoading: boolean;
    data: IUser[];
    dataMap?: Map<number, IUser>;
    error: null | SerializedError;
}

export interface Users {
    company: ICompanyUsersState;
    adminPortal: IEntityState<IUser[]>;
}

export interface AdminPanelState {
    companies: IEntityState<ICompany[]>;
    users: Users;
    adminPortalUsersPaginated: {
        data: any[];
        count: number;
        isLoading: boolean;
        lastRefresh: string | null;
        stats: { beneficiary: number; publisher: number; admin: number } | null;
    };
    lastAddedUser: IEntityState<any>;
    lastAddedCompany: IEntityState<any>;
    logList: ILog[];
    countLog: number;
    logListStatus: TFetchStatus;
    syncTCPDStatus: TFetchStatus;
    appMaintenances: AppMaintenanceWindow[];
    appMaintenanceStatus: TFetchStatus;
    saveAppMaintenanceStatus: TFetchStatus;
}

const initialState: AdminPanelState = {
    companies: {
        isLoading: false,
        data: [],
        error: null,
    },
    users: {
        company: {
            isLoading: false,
            data: [],
            dataMap: new Map(),
            error: null,
        },
        adminPortal: {
            isLoading: false,
            data: [],
            error: null,
        },
    },
    adminPortalUsersPaginated: {
        data: [],
        count: 0,
        isLoading: false,
        lastRefresh: null,
        stats: null,
    },
    lastAddedUser: {
        isLoading: false,
        data: null,
        error: null,
    },
    lastAddedCompany: {
        isLoading: false,
        data: null,
        error: null,
    },
    logList: [],
    countLog: 0,
    logListStatus: "idle",
    syncTCPDStatus: "idle",
    appMaintenances: [],
    appMaintenanceStatus: "idle",
    saveAppMaintenanceStatus: "idle",
};

export const adminPanelSlice = createSlice({
    name: "adminPanel",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // sendInvitation
        builder.addCase(sendInvitation.pending, (state, action) => {
            state.lastAddedUser.isLoading = true;
            state.lastAddedUser.error = null;
            state.lastAddedUser.data = null;
        });
        builder.addCase(sendInvitation.fulfilled, (state, action) => {
            toast.success(`${t("AdminPanel.AddUserSuccess")}`);
            state.lastAddedUser.isLoading = false;
            const payload = action.payload;
            state.lastAddedUser.data = payload;
            const currentUsers = JSON.parse(JSON.stringify(state.users.company.data));
            currentUsers.push({ ...payload, role: action.meta.arg.role });
            state.users.company.data = currentUsers;
            state.users.company.dataMap = new Map(currentUsers.map((user) => [+user.id, user]));
        });
        builder.addCase(sendInvitation.rejected, (state, { payload, error }) => {
            if ((payload as any).error === "An account with the given email already exists.") {
                toast.error(`${t("AdminPanel.MailExists")}`);
            } else {
                toastApiError(payload);
            }
            state.lastAddedUser.isLoading = false;
            state.lastAddedUser.error = error;
        });
        // createCompany
        builder.addCase(createCompany.pending, (state, action) => {
            state.lastAddedCompany.isLoading = true;
            state.lastAddedCompany.error = null;
            state.lastAddedCompany.data = null;
        });
        builder.addCase(createCompany.fulfilled, (state, { payload }) => {
            toast.success(`${t("AdminPanel.CreateCompanySuccess")}`);
            state.lastAddedCompany.isLoading = false;
            state.lastAddedCompany.data = payload;
        });
        builder.addCase(createCompany.rejected, (state, { payload, error }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.lastAddedCompany.isLoading = false;
            state.lastAddedCompany.error = error;
        });
        //getCompanyList
        builder.addCase(getCompanyList.pending, (state, action) => {
            state.companies.isLoading = true;
            state.companies.error = null;
        });

        builder.addCase(getCompanyList.fulfilled, (state, { payload }) => {
            state.companies.isLoading = false;
            state.companies.data = payload;
        });
        builder.addCase(getCompanyList.rejected, (state, { payload, error }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.companies.isLoading = false;
            state.companies.error = error;
        });
        //getUsrList
        builder.addCase(getAdminPortalUserList.pending, (state, action) => {
            state.users.adminPortal.isLoading = true;
            state.users.adminPortal.error = null;
        });
        builder.addCase(getAdminPortalUserList.fulfilled, (state, { payload }) => {
            state.users.adminPortal.isLoading = false;
            state.users.adminPortal.data = payload;
        });
        builder.addCase(getAdminPortalUserList.rejected, (state, { payload, error }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.users.adminPortal.isLoading = false;
            state.users.adminPortal.error = error;
        });
        // getUserListByCompany
        builder.addCase(getUserListByCompany.pending, (state, action) => {
            state.users.company.isLoading = true;
            state.users.company.error = null;
        });
        builder.addCase(getUserListByCompany.fulfilled, (state, { payload }) => {
            state.users.company.isLoading = false;
            state.users.company.data = payload;
            state.users.company.dataMap = new Map(payload.map((user) => [+user.id, user]));
        });
        builder.addCase(getUserListByCompany.rejected, (state, { payload, error }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.users.company.isLoading = false;
            state.users.company.error = error;
        });
        // DeleteUser
        builder.addCase(deleteUser.pending, () => {});
        builder.addCase(deleteUser.fulfilled, (state, { payload }) => {
            toast.success(`${t("AdminPanel.DeleteUserSuccess")}`);
            const currentUsers = JSON.parse(JSON.stringify(state.users.company.data));
            const newUsers = currentUsers.filter((item: any) => item.uuid != payload);
            state.users.company.data = newUsers;
            state.users.company.dataMap = new Map(newUsers.map((user) => [+user.id, user]));
        });
        builder.addCase(deleteUser.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        // changeAdminCompany
        builder.addCase(changeAdminCompany.pending, () => {});
        builder.addCase(changeAdminCompany.fulfilled, () => {
            toast.success(`${t("AdminPanel.ChangeCompanyInfoSuccess")}`);
        });
        builder.addCase(changeAdminCompany.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        // DeleteCompany
        builder.addCase(eraseData.pending, () => {});
        builder.addCase(eraseData.fulfilled, () => {
            toast.success(`${t("AdminPanel.DeleteCompanySuccess")}`);
        });
        builder.addCase(eraseData.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        // MigrationCompany
        builder.addCase(migrationCompany.pending, () => {});
        builder.addCase(migrationCompany.fulfilled, () => {
            toast.success(`${t("AdminPanel.MigrationCompanySuccess")}`);
        });
        builder.addCase(migrationCompany.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        // Admin portal users paginated
        builder.addCase(getAdminPortalUserListPaginated.pending, (state) => {
            state.adminPortalUsersPaginated.isLoading = true;
        });
        builder.addCase(getAdminPortalUserListPaginated.fulfilled, (state, { payload }) => {
            state.adminPortalUsersPaginated.data = payload.result ?? [];
            state.adminPortalUsersPaginated.count = payload.count ?? 0;
            state.adminPortalUsersPaginated.lastRefresh = payload.lastRefresh ?? null;
            state.adminPortalUsersPaginated.stats = payload.stats ?? null;
            state.adminPortalUsersPaginated.isLoading = false;
        });
        builder.addCase(getAdminPortalUserListPaginated.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.adminPortalUsersPaginated.data = [];
            state.adminPortalUsersPaginated.count = 0;
            state.adminPortalUsersPaginated.isLoading = false;
        });
        // Log list
        builder.addCase(getLogList.pending, (state) => {
            state.logListStatus = "pending";
        });
        builder.addCase(getLogList.fulfilled, (state, action) => {
            state.logList = action.payload.entries;
            state.countLog = action.payload.count;
            state.logListStatus = "succeeded";
        });
        builder.addCase(getLogList.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
            state.logList = [];
            state.countLog = 0;
            state.logListStatus = "failed";
        });
        builder.addCase(updateUserRole.fulfilled, (state, { payload }) => {
            const currentUsers = JSON.parse(JSON.stringify(state.users.company.data));
            state.users.company.data = currentUsers.map((user) => {
                if (user.id === payload.id) {
                    return { ...user, role: payload.role };
                } else {
                    return user;
                }
            });
            state.users.company.dataMap = new Map(currentUsers.map((user) => [+user.id, user]));
            toast.success(`${t("Members.RoleChangeValid")}`);
        });
        builder.addCase(updateUserRole.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(autofillCompany.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(autofillCompany.fulfilled, (_state, _action) => {
            toast.success("The company has been successfully autofilled");
        });
        builder.addCase(askForSeats.fulfilled, (_state, _action) => {
            toast.success(`${t("Members.SuccessfulRequest")}`);
        });
        builder.addCase(askForSeats.rejected, (_state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(updateUser.fulfilled, (state, action) => {
            const userId = action.payload.id;
            state.users.company.data = state.users.company.data.map((user) =>
                user.userId === userId ? { ...user, ...action.payload } : user,
            );
            state.users.company.dataMap = new Map(state.users.company.data.map((user) => [+user.id, user]));
            toast.success(`${t("Members.UpdateUserSuccess")}`);
        });
        builder.addCase(updateUser.rejected, (_state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(syncTCPD.pending, (state) => {
            state.syncTCPDStatus = "pending";
        });
        builder.addCase(syncTCPD.fulfilled, (state) => {
            state.syncTCPDStatus = "succeeded";
            toast.success("TCPD sync completed successfully");
        });
        builder.addCase(syncTCPD.rejected, (state, { payload }) => {
            state.syncTCPDStatus = "failed";
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(recomputeStocks.fulfilled, () => {
            toast.success("Stock recomputation completed successfully");
        });
        builder.addCase(recomputeStocks.rejected, (state, { payload }) => {
            toastApiError(payload, "ServerError.InternalServerError");
        });
        builder.addCase(fetchAppMaintenance.pending, (state) => {
            state.appMaintenanceStatus = "pending";
        });
        builder.addCase(fetchAppMaintenance.fulfilled, (state, { payload }) => {
            state.appMaintenanceStatus = "fulfilled";
            state.appMaintenances = payload;
        });
        builder.addCase(fetchAppMaintenance.rejected, (state, { payload }) => {
            state.appMaintenanceStatus = "rejected";
            toastApiError(payload, "appMaintenance.fetch");
        });
        builder.addCase(createAppMaintenance.pending, (state) => {
            state.saveAppMaintenanceStatus = "pending";
        });
        builder.addCase(createAppMaintenance.fulfilled, (state, { payload }) => {
            state.saveAppMaintenanceStatus = "fulfilled";
            state.appMaintenances = [payload, ...state.appMaintenances.filter((window) => window.id !== payload.id)];
            toast.success(`${t("AdminPanel.MaintenanceSaved")}`);
        });
        builder.addCase(createAppMaintenance.rejected, (state, { payload }) => {
            state.saveAppMaintenanceStatus = "rejected";
            toastApiError(payload, "appMaintenance.save");
        });
        builder.addCase(cancelAppMaintenance.fulfilled, (state, { payload }) => {
            state.appMaintenances = state.appMaintenances.map((window) =>
                window.id === payload.id ? payload : window,
            );
            toast.success(`${t("AdminPanel.MaintenanceCancelledToast")}`);
        });
        builder.addCase(cancelAppMaintenance.rejected, (_state, { payload }) => {
            toastApiError(payload, "appMaintenance.cancel");
        });
        builder.addCase(finishAppMaintenance.fulfilled, (state, { payload }) => {
            state.appMaintenances = state.appMaintenances.map((window) =>
                window.id === payload.id ? payload : window,
            );
            toast.success(`${t("AdminPanel.MaintenanceFinishedToast")}`);
        });
        builder.addCase(finishAppMaintenance.rejected, (_state, { payload }) => {
            toastApiError(payload, "appMaintenance.finish");
        });
    },
});

export default adminPanelSlice.reducer;
